import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { 
  Bot, Plus, RefreshCw, CheckCircle, XCircle, AlertTriangle, 
  Trash2, Edit, Phone, Building, User, Link2 
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminAgentsPage() {
  const { adminKey } = useOutletContext();
  const [agents, setAgents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [formData, setFormData] = useState({
    agent_id: "",
    client_id: "",
    location_id: "",
    sip_number: "",
    display_name: ""
  });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [adminKey]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { "X-Admin-Key": adminKey };
      
      const [agentsRes, clientsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/agents`, { headers }),
        fetch(`${API_URL}/api/admin/clients?limit=500`, { headers })
      ]);

      if (agentsRes.ok) {
        const data = await agentsRes.json();
        setAgents(data.agents || []);
      }

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAgent = async (e) => {
    e.preventDefault();
    setFormError("");
    
    if (!formData.agent_id.trim()) {
      setFormError("Agent ID is required (e.g., sig_abc123)");
      return;
    }
    if (!formData.client_id) {
      setFormError("Please select a client");
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${API_URL}/api/agents`, {
        method: "POST",
        headers: {
          "X-Admin-Key": adminKey,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          agent_id: formData.agent_id.trim(),
          client_id: formData.client_id,
          location_id: formData.location_id.trim() || null,
          sip_number: formData.sip_number.trim() || null,
          display_name: formData.display_name.trim() || null
        })
      });

      if (res.ok) {
        setShowCreateModal(false);
        setFormData({ agent_id: "", client_id: "", location_id: "", sip_number: "", display_name: "" });
        fetchData();
        alert("Agent created successfully!");
      } else {
        const err = await res.json();
        setFormError(err.detail || "Failed to create agent");
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();
    setFormError("");

    try {
      setSubmitting(true);
      const res = await fetch(`${API_URL}/api/agents/${selectedAgent.agent_id}`, {
        method: "PATCH",
        headers: {
          "X-Admin-Key": adminKey,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_id: formData.client_id || undefined,
          location_id: formData.location_id.trim() || undefined,
          sip_number: formData.sip_number.trim() || undefined,
          display_name: formData.display_name.trim() || undefined
        })
      });

      if (res.ok) {
        setShowEditModal(false);
        setSelectedAgent(null);
        fetchData();
        alert("Agent updated successfully!");
      } else {
        const err = await res.json();
        setFormError(err.detail || "Failed to update agent");
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivateAgent = async (agentId) => {
    if (!confirm(`Are you sure you want to deactivate agent ${agentId}? This will stop all calls to this agent.`)) {
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/agents/${agentId}`, {
        method: "DELETE",
        headers: { "X-Admin-Key": adminKey }
      });

      if (res.ok) {
        fetchData();
        alert("Agent deactivated successfully");
      } else {
        const err = await res.json();
        alert(`Error: ${err.detail || "Failed to deactivate agent"}`);
      }
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const openEditModal = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      agent_id: agent.agent_id,
      client_id: agent.client_id || "",
      location_id: agent.location_id || "",
      sip_number: agent.sip_number || "",
      display_name: agent.display_name || ""
    });
    setFormError("");
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setFormData({ agent_id: "", client_id: "", location_id: "", sip_number: "", display_name: "" });
    setFormError("");
    setShowCreateModal(true);
  };

  // Get client name by ID
  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || clientId || "Unknown";
  };

  // Stats
  const activeAgents = agents.filter(a => a.status === "active").length;
  const orphanedAgents = agents.filter(a => !a.client_id).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Agent Management</h1>
          <p className="text-gray-500 text-sm mt-1">Map SigmaMind agents to Calyro clients</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button 
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-gray-800 rounded-lg hover:from-emerald-700 hover:to-teal-700 font-semibold"
          >
            <Plus className="w-4 h-4" /> Create Agent Mapping
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard title="Total Agents" value={agents.length} icon={Bot} color="blue" />
        <StatCard title="Active" value={activeAgents} icon={CheckCircle} color="green" />
        <StatCard 
          title="Orphaned (No Client)" 
          value={orphanedAgents} 
          icon={AlertTriangle} 
          color={orphanedAgents > 0 ? "orange" : "green"} 
        />
      </div>

      {/* Agents Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-gray-800 font-semibold">Agent Mappings ({agents.length})</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : agents.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Bot className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No agents configured</p>
            <p className="text-sm mt-2">Create your first agent mapping to start accepting calls</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Agent ID</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">SIP Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {agents.map((agent, idx) => (
                  <tr key={agent.agent_id || idx} className="hover:bg-gray-50/30">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-blue-600" />
                        <div>
                          <p className="text-gray-800 font-mono text-sm">{agent.agent_id}</p>
                          <p className="text-gray-500 text-xs">{agent.display_name || "No display name"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {agent.client_id ? (
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-500" />
                          <div>
                            <p className="text-gray-800 text-sm">{getClientName(agent.client_id)}</p>
                            <p className="text-gray-400 text-xs font-mono">{agent.client_id?.slice(0, 12)}...</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-orange-400 text-sm flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" /> Not mapped
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {agent.sip_number ? (
                        <span className="flex items-center gap-1 text-gray-600">
                          <Phone className="w-4 h-4 text-green-600" />
                          {agent.sip_number}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {agent.location_id ? (
                        <span className="flex items-center gap-1 text-gray-600">
                          <Building className="w-4 h-4 text-purple-400" />
                          {agent.location_id}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={agent.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => openEditModal(agent)}
                          className="p-2 hover:bg-blue-600 rounded-lg text-blue-600 hover:text-gray-800"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        {agent.status === "active" && (
                          <button 
                            onClick={() => handleDeactivateAgent(agent.agent_id)}
                            className="p-2 hover:bg-red-600 rounded-lg text-red-600 hover:text-gray-800"
                            title="Deactivate"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Agent Modal */}
      {showCreateModal && (
        <Modal title="Create Agent Mapping" onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreateAgent} className="space-y-4">
            <p className="text-gray-500 text-sm mb-4">
              Link a SigmaMind agent to a Calyro client. This enables call routing and billing.
            </p>

            <div>
              <label className="block text-gray-600 text-sm mb-1">
                Agent ID <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <Bot className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.agent_id}
                  onChange={(e) => setFormData(f => ({ ...f, agent_id: e.target.value }))}
                  placeholder="sig_abc123xyz"
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <p className="text-gray-400 text-xs mt-1">The agent ID from SigmaMind platform</p>
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">
                Client <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <select
                  value={formData.client_id}
                  onChange={(e) => setFormData(f => ({ ...f, client_id: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="">Select a client...</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.name || "Unnamed"} ({client.id?.slice(0, 8)}...)
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">SIP Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.sip_number}
                  onChange={(e) => setFormData(f => ({ ...f, sip_number: e.target.value }))}
                  placeholder="+1-555-123-4567"
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <p className="text-gray-400 text-xs mt-1">The phone number assigned to this agent</p>
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">Location ID</label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={formData.location_id}
                  onChange={(e) => setFormData(f => ({ ...f, location_id: e.target.value }))}
                  placeholder="loc_downtown_office"
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <p className="text-gray-400 text-xs mt-1">For multi-location clients (optional)</p>
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">Display Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData(f => ({ ...f, display_name: e.target.value }))}
                placeholder="Main Reception Line"
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>

            {formError && (
              <div className="p-3 bg-red-100 border border-red-200 rounded-lg text-red-300 text-sm">
                {formError}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button 
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button 
                type="submit"
                disabled={submitting}
                className="flex-1 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-gray-800 rounded-lg hover:from-emerald-700 hover:to-teal-700 font-semibold disabled:opacity-50"
              >
                {submitting ? "Creating..." : "Create Agent"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Agent Modal */}
      {showEditModal && selectedAgent && (
        <Modal title="Edit Agent Mapping" onClose={() => setShowEditModal(false)}>
          <form onSubmit={handleUpdateAgent} className="space-y-4">
            <div className="bg-gray-50/50 rounded-lg p-3 mb-4">
              <p className="text-gray-600 text-sm">
                <strong>Agent ID:</strong> <span className="font-mono">{selectedAgent.agent_id}</span>
              </p>
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">Client</label>
              <select
                value={formData.client_id}
                onChange={(e) => setFormData(f => ({ ...f, client_id: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a client...</option>
                {clients.map(client => (
                  <option key={client.id} value={client.id}>
                    {client.name || "Unnamed"} ({client.id?.slice(0, 8)}...)
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">SIP Number</label>
              <input
                type="text"
                value={formData.sip_number}
                onChange={(e) => setFormData(f => ({ ...f, sip_number: e.target.value }))}
                placeholder="+1-555-123-4567"
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">Location ID</label>
              <input
                type="text"
                value={formData.location_id}
                onChange={(e) => setFormData(f => ({ ...f, location_id: e.target.value }))}
                placeholder="loc_downtown_office"
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-gray-600 text-sm mb-1">Display Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => setFormData(f => ({ ...f, display_name: e.target.value }))}
                placeholder="Main Reception Line"
                className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {formError && (
              <div className="p-3 bg-red-100 border border-red-200 rounded-lg text-red-300 text-sm">
                {formError}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button 
                type="button"
                onClick={() => setShowEditModal(false)}
                className="flex-1 py-2 bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button 
                type="submit"
                disabled={submitting}
                className="flex-1 py-2 bg-blue-600 text-gray-800 rounded-lg hover:bg-blue-700 font-semibold disabled:opacity-50"
              >
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colors = {
    blue: "from-blue-600 to-blue-700",
    green: "from-emerald-600 to-emerald-700",
    orange: "from-orange-600 to-orange-700",
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm">{title}</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-gray-800" />
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const getStyle = () => {
    switch (status) {
      case "active": return "bg-green-100 text-green-600 border-green-200";
      case "inactive": return "bg-gray-50 text-gray-500 border-gray-300";
      default: return "bg-gray-50 text-gray-600 border-gray-300";
    }
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStyle()}`}>
      {status || "unknown"}
    </span>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6 border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <XCircle className="w-6 h-6" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export default AdminAgentsPage;
